import { PublicApiBuild } from '@/models/Build';

export const buildLogSummaryPath = (appSlug: string, buildSlug: PublicApiBuild['slug']) =>
  `/v0.1/apps/${appSlug}/builds/${buildSlug}/log/summary`;

export const buildEnvironmentSetupPath = (projectSlug: string, buildSlug: string) =>
  `/v0.1/apps/${projectSlug}/builds/${buildSlug}/log/build-environment-setup`;

export const stepLogPath = (appSlug: string, buildSlug: string, stepUuid: string) =>
  `/v0.1/apps/${appSlug}/builds/${buildSlug}/log/steps/${stepUuid}`;

export const appPipelinesPath = (
  appSlug: string,
  params?: { branch?: string; workflow?: string; status?: number; limit?: number },
) => {
  const url = `/v0.1/apps/${appSlug}/pipelines`;

  const searchParams = new URLSearchParams(
    Object.entries(params || {})
      .filter(([, value]) => value !== undefined)
      .map(([key, value]) => [key, String(value)]),
  );

  const paramString = searchParams.toString();

  return paramString ? `${url}?${paramString}` : url;
};

export const appBranchesPath = (appSlug: string, filter = '', limit = 0) => {
  const url = `/v0.1/apps/${appSlug}/pipelines/active-branches`;
  const searchParams = new URLSearchParams();

  if (limit) {
    searchParams.append('limit', String(limit));
  }

  if (filter) {
    searchParams.append('branch', String(filter));
  }

  const paramString = searchParams.toString();

  return paramString ? `${url}?${paramString}` : url;
};

export const awsControllersPath = (slug: string) => {
  return `/v0.1/platform/organization/${slug}/aws_controllers`;
};

export const awsControllerPath = (workspaceSlug: string, awsControllerId: string) => {
  return `/v0.1/platform/organization/${workspaceSlug}/aws_controllers/${awsControllerId}`;
};

export const agentPoolsPath = (slug: string, filterParams?: Record<string, string>) => {
  const queryParam = filterParams ? `?${new URLSearchParams(filterParams).toString()}` : '';
  return `/v0.1/platform/organization/${slug}/agent_pools${queryParam}`;
};

export const agentPoolPath = (slug: string, agentPoolId: string) => {
  return `/v0.1/platform/organization/${slug}/agent_pools/${agentPoolId}`;
};

export const awsMachinePoolsPath = (slug: string, filterParams?: Record<string, string>) => {
  const queryParam = filterParams ? `?${new URLSearchParams(filterParams).toString()}` : '';
  return `/v0.1/platform/organization/${slug}/aws_machine_pools${queryParam}`;
};

export const awsMachinePoolPath = (workspaceSlug: string, poolId: string) => {
  return `/v0.1/platform/organization/${workspaceSlug}/aws_machine_pools/${poolId}`;
};

export const validateAwsAmiPath = (workspaceSlug: string) => {
  return `/v0.1/platform/organization/${workspaceSlug}/aws_ami_validate`;
};

export const listAwsInstanceTypesPath = (workspaceSlug: string, filterParams?: Record<string, string>) => {
  const queryParam = filterParams ? `?${new URLSearchParams(filterParams).toString()}` : '';
  return `/v0.1/platform/organization/${workspaceSlug}/aws_instance_types${queryParam}`;
};

export const listCiToolConnectionsPath = (workspaceSlug: string) => {
  return `/build-hub/organization/${workspaceSlug}/connections`;
};

export const createCiToolConnectionPath = (workspaceSlug: string, provider: string) => {
  return `/build-hub/organization/${workspaceSlug}/connections/${provider}`;
};

export const listGithubAppConnectionRepositoriesPath = (workspaceSlug: string, connectionId: string) => {
  return `/build-hub/organization/${workspaceSlug}/connections/github_app/${connectionId}/repositories`;
};

export const listMachineTypesPath = (workspaceSlug: string) => {
  return `/build-hub/organization/${workspaceSlug}/machine_types`;
};

export const listImagesPath = (workspaceSlug: string) => {
  return `/build-hub/organization/${workspaceSlug}/images`;
};

export const buildHubAccessPath = (workspaceSlug: string) => {
  return `/build-hub/organization/${workspaceSlug}/access`;
};

export const buildHubComputeResourceOverviewPath = (workspaceSlug: string) => {
  return `/build-hub/organization/${workspaceSlug}/compute_resource_overview`;
};

export const machinePoolsPath = (slug: string, filterParams?: Record<string, string>) => {
  const queryParam = filterParams ? `?${new URLSearchParams(filterParams).toString()}` : '';
  return `/build-hub/organization/${slug}/machine_pools${queryParam}`;
};

export const machinePoolPath = (workspaceSlug: string, poolId: string) => {
  return `/build-hub/organization/${workspaceSlug}/machine_pools/${poolId}`;
};

export const machinePath = (workspaceSlug: string, machineId: string) => {
  return `/build-hub/organization/${workspaceSlug}/machines/${machineId}?force_stop=false`;
};

export const machineRemoteAccessPath = (workspaceSlug: string, machineId: string) => {
  return `/build-hub/organization/${workspaceSlug}/machines/${machineId}/remote_access`;
};

export const machineLogsPath = (workspaceSlug: string, machineId: string, stage: string, type: string) => {
  return `/build-hub/organization/${workspaceSlug}/machines/${machineId}/logs/${stage}/${type}`;
};

export const listEnvironmentsPath = (workspaceSlug: string) => {
  return `/infrastructure/remote-execution/v1/workspaces/${workspaceSlug}/environments`;
};

export const listRemoteExecutionImagesPath = (workspaceSlug: string) => {
  return `/infrastructure/remote-execution/v1/workspaces/${workspaceSlug}/images`;
};

export const listRemoteExecutionMachineTypesPath = (workspaceSlug: string) => {
  return `/infrastructure/remote-execution/v1/workspaces/${workspaceSlug}/machine_types`;
};

export const deleteRemoteExecutionMachinePath = (workspaceSlug: string, machineId: string) => {
  return `/infrastructure/remote-execution/v1/workspaces/${workspaceSlug}/machines/${machineId}?force_stop=false`;
};

export const remoteExecutionMachineLogsPath = (
  workspaceSlug: string,
  machineId: string,
  stage: string,
  type: string,
) => {
  return `/infrastructure/remote-execution/v1/workspaces/${workspaceSlug}/machines/${machineId}/logs/${stage}/${type}`;
};

export const remoteExecutionMachineRemoteAccessPath = (workspaceSlug: string, machineId: string) => {
  return `/infrastructure/remote-execution/v1/workspaces/${workspaceSlug}/machines/${machineId}/remote_access`;
};

export const remoteExecutionWorkerPoolsPath = (workspaceSlug: string, filterParams?: Record<string, string>) => {
  const queryParam = filterParams ? `?${new URLSearchParams(filterParams).toString()}` : '';
  return `/infrastructure/remote-execution/v1/workspaces/${workspaceSlug}/worker_pools${queryParam}`;
};

export const remoteExecutionWorkerPoolPath = (workspaceSlug: string, poolId: string) => {
  return `/infrastructure/remote-execution/v1/workspaces/${workspaceSlug}/worker_pools/${poolId}`;
};

export const appsPath = (resource: string, accountSlug: string) => {
  return `/v0.1/${resource}/${accountSlug}/apps`;
};

export const platformLicensePoolsPath = (slug: string, licensePoolId?: string) => {
  return `/v0.1/platform/organization/${slug}/license_pools${licensePoolId ? `/${licensePoolId}` : ''}`;
};

export const manualApprovalBuildListPath = (slug: string) => {
  return `/v0.1/apps/${slug}/build-requests`;
};

export const manualApprovalBuildPath = (appSlug: string, buildRequestSlug: string) => {
  return `/v0.1/apps/${appSlug}/build-requests/${buildRequestSlug}`;
};
